import React, { useMemo } from 'react';
import { ReportSection } from '../framework';
import { CalloutBox } from '../primitives';
import { ScenarioToggle } from '../widgets';
import { ComparisonBar } from '../widgets';
import { WealthChart } from '../../charts/WealthChart';
import { ScenarioPreset, scenarioLabels } from '../data';
import { EnhancedAssumptions } from '../../../types';

interface ComparisonSectionProps {
  assumptions: EnhancedAssumptions;
  metrics: {
    year10RentValue: number; // Net liquidation value
    year10SellValue: number; // Net liquidation value
    year10RentGrossEquity: number;
    year10SellGrossValue: number;
    saleProceeds: number;
  };
  results: Array<{
    year: number;
    rentScenario: {
      totalEquityReal: number;
      netLiquidationValue: number;
      propertyValue: number;
      cumulativeReinvestedIncome: number;
      depreciationRecaptureTax: number;
      capitalGainsTax: number;
      sellingCostsAtExit: number;
    };
    sellScenarios: {
      portfolio80_20: { totalValueReal: number; netLiquidationValue: number };
      cumulativeTaxDrag: number;
    };
  }>;
  rentAdvantage: number;
  formatCurrency: (value: number) => string;
  scenarioPreset: ScenarioPreset;
  onScenarioChange: (preset: ScenarioPreset) => void;
}

// Tax constants (matching main calculations)
const FEDERAL_DEPRECIATION_RECAPTURE_RATE = 0.25;
const CA_STATE_TAX_RATE = 0.123;
const PROP_13_MAX_GROWTH_RATE = 0.02;
const SP500_DIVIDEND_YIELD = 0.013;

// Helper to calculate 10-year rent outcome with custom assumptions
// Returns NET LIQUIDATION VALUE (after all exit taxes and costs)
const calcRentOutcome = (assumptions: EnhancedAssumptions, overrides: Partial<EnhancedAssumptions> = {}): number => {
  const a = { ...assumptions, ...overrides };
  const basePropertyValue = a.propertyValue;
  const appreciation = a.propertyAppreciation;
  const year10PropertyValue = basePropertyValue * Math.pow(1 + appreciation / 100, 10);

  // Depreciation (25% building value over 27.5 years)
  const buildingValue = basePropertyValue * 0.25;
  const annualDepreciation = buildingValue / 27.5;
  const totalDepreciation = annualDepreciation * 10;

  // Tax drag on reinvested rental income (same as sell scenario)
  const dividendTaxRate = a.capitalGainsRate / 100;
  const bondInterestTaxRate = a.rentalIncomeTaxRate / 100;
  const equityDividendDrag = SP500_DIVIDEND_YIELD * dividendTaxRate;
  const bondInterestDrag = 0.04 * bondInterestTaxRate;
  const effectiveStockReturn = a.stockReturns - (equityDividendDrag * 100);
  const effectiveBondReturn = a.bondReturns - (bondInterestDrag * 100);
  const blendedReturn = (0.8 * effectiveStockReturn) + (0.2 * effectiveBondReturn);

  // Prop 13: assessed value grows at max 2%/year nominal after reassessment
  const nominalAppreciation = appreciation / 100 + a.inflationRate / 100;
  const assessedGrowthRate = Math.min(nominalAppreciation, PROP_13_MAX_GROWTH_RATE);

  let portfolio = 0;
  const taxRate = a.rentalIncomeTaxRate / 100;

  for (let year = 1; year <= 10; year++) {
    const yearPropertyValue = basePropertyValue * Math.pow(1 + appreciation / 100, year);
    // Use assessed value (Prop 13 capped) for property tax, not market value
    const yearAssessedValue = basePropertyValue * Math.pow(1 + assessedGrowthRate, year);
    const yearRent = a.monthlyRent * 12 * Math.pow(1 + a.rentGrowth / 100, year);
    const vacancyLoss = yearRent * a.vacancyRate / 100;
    const grossRentalIncome = yearRent - vacancyLoss;

    const propertyTax = yearAssessedValue * a.propertyTaxRate / 100; // Based on assessed, not market
    const maintenance = yearPropertyValue * a.maintenanceRate / 100;
    const managementCost = grossRentalIncome * a.managementFee / 100;

    const netRentalIncome = grossRentalIncome - propertyTax - a.insuranceCost - maintenance - managementCost;

    const taxableIncome = Math.max(0, netRentalIncome - annualDepreciation);
    const tax = taxableIncome * taxRate;
    const afterTax = netRentalIncome - tax;

    portfolio = portfolio * (1 + blendedReturn / 100) + afterTax;
  }

  const grossEquity = year10PropertyValue + portfolio - a.rentalSetupCosts;

  // Exit costs and taxes
  const depreciationRecaptureTax = totalDepreciation * (FEDERAL_DEPRECIATION_RECAPTURE_RATE + CA_STATE_TAX_RATE);
  const appreciationSinceInheritance = year10PropertyValue - basePropertyValue;
  const capitalGainsTax = appreciationSinceInheritance > 0 ? appreciationSinceInheritance * (a.capitalGainsRate / 100) : 0;
  const sellingCostsAtExit = year10PropertyValue * (a.sellingCosts / 100);

  return grossEquity - depreciationRecaptureTax - capitalGainsTax - sellingCostsAtExit;
};

// Helper to calculate 10-year sell outcome with custom assumptions
// Returns NET LIQUIDATION VALUE (after tax drag and capital gains if sold)
const calcSellOutcome = (assumptions: EnhancedAssumptions, overrides: Partial<EnhancedAssumptions> = {}): number => {
  const a = { ...assumptions, ...overrides };
  const saleProceeds = a.propertyValue * (1 - a.sellingCosts / 100);

  // Tax drag on portfolio returns
  const dividendTaxRate = a.capitalGainsRate / 100;
  const bondInterestTaxRate = a.rentalIncomeTaxRate / 100;
  const equityDividendDrag = SP500_DIVIDEND_YIELD * dividendTaxRate;
  const bondInterestDrag = 0.04 * bondInterestTaxRate; // ~4% bond yield

  // Calculate after-tax returns
  const effectiveStockReturn = a.stockReturns - (equityDividendDrag * 100);
  const effectiveBondReturn = a.bondReturns - (bondInterestDrag * 100);
  const blendedReturn = (0.8 * effectiveStockReturn) + (0.2 * effectiveBondReturn);

  const portfolioValue = saleProceeds * Math.pow(1 + blendedReturn / 100, 10);

  // Capital gains tax if fully liquidated
  const gain = portfolioValue - saleProceeds;
  const capGainsTax = gain > 0 ? gain * (a.capitalGainsRate / 100) : 0;

  return portfolioValue - capGainsTax;
};

const ScenarioDetail: React.FC<{ preset: ScenarioPreset }> = ({ preset }) => {
  const details: Record<ScenarioPreset, { title: string; description: string; stocks: string; property: string; rent: string; plausibility: string }> = {
    bothStagnate: {
      title: 'Both Stagnate Scenario',
      description: 'A pessimistic scenario where neither asset class performs well. Stocks struggle due to high valuations and economic headwinds, while real estate cools from its recent highs. This is the "lost decade" scenario—possible but not the base case.',
      stocks: '4.0%',
      property: '2.0%',
      rent: '0.0%',
      plausibility: 'The 2000s saw stocks return -2.8% real while housing had mixed results. High CAPE ratios historically predict weak forward returns. A recession, persistent inflation, or geopolitical shock could create this environment. In this scenario, the choice matters less—both options underperform expectations.'
    },
    stocksWin: {
      title: 'Stocks Outperform Scenario',
      description: 'This scenario assumes stocks beat their historical average while Newport Beach real estate reverts to national norms. Plausible if you believe: current high CAPE is sustainable due to tech/AI productivity gains, but coastal CA real estate is overvalued and due for a correction.',
      stocks: '8.5%',
      property: '2.3%',
      rent: '0.5%',
      plausibility: 'Newport Beach prices have risen 80-100% since 2019. Mean reversion suggests slower growth ahead. Meanwhile, S&P 500 earnings growth from AI could sustain higher multiples. National Case-Shiller averages 2.3% real over 35 years—Newport reverting to this isn\'t extreme.'
    },
    balanced: {
      title: 'Balanced Scenario',
      description: 'This scenario uses historical averages that are defensible but not guaranteed. LA regional appreciation rather than national (recognizing California\'s structural advantages), and long-term stock returns despite currently high valuations.',
      stocks: '7.5%',
      property: '3.5%',
      rent: '1.0%',
      plausibility: 'These numbers reflect what has actually happened over long periods. LA has outperformed national housing by ~1% annually; using this rather than Newport-specific data is conservative since Newport has outperformed LA. Stock returns use the full historical record.'
    },
    realEstateWins: {
      title: 'Real Estate Outperforms Scenario',
      description: 'This scenario assumes Newport Beach continues to outperform while stocks deliver below-average returns. Plausible if you believe: CAPE at 40 means lower future returns, and coastal CA\'s supply constraints + wealth migration patterns continue to drive premium appreciation.',
      stocks: '6.5%',
      property: '4.5%',
      rent: '1.5%',
      plausibility: 'High CAPE historically predicts ~5-6% real returns over the next decade. Newport Beach has supply constraints (ocean, built-out city) that most markets lack. California\'s Prop 13 reduces turnover, tightening supply further. Premium coastal markets often outperform even LA metro averages.'
    },
    bothBoom: {
      title: 'Both Boom Scenario',
      description: 'An optimistic scenario where both asset classes continue their 2020-2025 performance. The S&P 500 has averaged ~12.5% real returns over this period (including the 2022 drawdown). Coastal California real estate continues its exceptional run. This is the "everything works" scenario.',
      stocks: '12.5%',
      property: '8.0%',
      rent: '2.0%',
      plausibility: 'Recent performance extended. Requires continued AI-driven productivity growth for stocks AND continued wealth migration to coastal CA for real estate. Historically rare for both asset classes to boom simultaneously for a decade, but the 2010s came close.'
    }
  };

  const d = details[preset];

  return (
    <div className="mb-6 p-4 bg-gray-50 border border-gray-200">
      <p className="font-semibold text-gray-900 mb-2">{d.title}</p>
      <p className="text-sm text-gray-700 mb-3">{d.description}</p>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="bg-white p-3 border border-gray-200">
          <p className="text-xs text-gray-500">Stock Returns (Real)</p>
          <p className="font-bold text-gray-900">{d.stocks}</p>
          <p className="text-xs text-gray-500">vs 7.5% balanced</p>
        </div>
        <div className="bg-white p-3 border border-gray-200">
          <p className="text-xs text-gray-500">Property Appreciation (Real)</p>
          <p className="font-bold text-gray-900">{d.property}</p>
          <p className="text-xs text-gray-500">vs 3.5% balanced</p>
        </div>
        <div className="bg-white p-3 border border-gray-200">
          <p className="text-xs text-gray-500">Rent Growth (Real)</p>
          <p className="font-bold text-gray-900">{d.rent}</p>
          <p className="text-xs text-gray-500">vs 1.0% balanced</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        <strong>Why it's plausible:</strong> {d.plausibility}
      </p>
    </div>
  );
};

export const ComparisonSection: React.FC<ComparisonSectionProps> = ({
  assumptions,
  metrics,
  results,
  rentAdvantage,
  formatCurrency,
  scenarioPreset,
  onScenarioChange
}) => {
  // Get final year data for the breakdown
  const finalYear = results[results.length - 1];

  const wealthChartData = results.map(r => ({
    year: r.year,
    rentTotal: r.rentScenario.totalEquityReal,
    sell80_20: r.sellScenarios.portfolio80_20.totalValueReal,
  }));

  // Calculate baseline values
  const baselineRent = useMemo(() => calcRentOutcome(assumptions), [assumptions]);
  const baselineSell = useMemo(() => calcSellOutcome(assumptions), [assumptions]);

  // Calculate "What If" scenario impacts
  const whatIfScenarios = useMemo(() => {
    const scenarios = [
      {
        name: 'Stock Boom',
        desc: `S&P returns 10% instead of ${assumptions.stockReturns}%`,
        rentImpact: calcRentOutcome(assumptions, { stockReturns: 10 }) - baselineRent,
        sellImpact: calcSellOutcome(assumptions, { stockReturns: 10 }) - baselineSell,
        affectsRent: true, // Rental income is reinvested in stocks
        affectsSell: true,
      },
      {
        name: 'Stock Bust',
        desc: 'S&P returns 4% (high CAPE scenario)',
        rentImpact: calcRentOutcome(assumptions, { stockReturns: 4 }) - baselineRent,
        sellImpact: calcSellOutcome(assumptions, { stockReturns: 4 }) - baselineSell,
        affectsRent: true,
        affectsSell: true,
      },
      {
        name: 'NASDAQ Instead',
        desc: 'Invest in tech at 12% returns',
        rentImpact: calcRentOutcome(assumptions, { stockReturns: 12 }) - baselineRent,
        sellImpact: calcSellOutcome(assumptions, { stockReturns: 12 }) - baselineSell,
        affectsRent: true,
        affectsSell: true,
      },
      {
        name: 'Major Repair Needed',
        desc: '$80K roof or foundation work (1954 home)',
        rentImpact: -80000,
        sellImpact: 0,
        affectsRent: true,
        affectsSell: false,
        isTenantRisk: true,
      },
      {
        name: 'One Bad Tenant Year',
        desc: '6-month vacancy + $30K repairs/legal',
        rentImpact: -(assumptions.monthlyRent * 6 + 30000),
        sellImpact: 0,
        affectsRent: true,
        affectsSell: false,
        isTenantRisk: true,
      },
      {
        name: 'Two Bad Tenant Years',
        desc: 'Happens to ~15% of landlords over 10 years',
        rentImpact: -(assumptions.monthlyRent * 12 + 60000),
        sellImpact: 0,
        affectsRent: true,
        affectsSell: false,
        isTenantRisk: true,
      },
      {
        name: 'Newport Correction',
        desc: 'Appreciation drops to 2% (national average)',
        rentImpact: calcRentOutcome(assumptions, { propertyAppreciation: 2 }) - baselineRent,
        sellImpact: 0,
        affectsRent: true,
        affectsSell: false,
      },
      {
        name: 'Newport Boom Continues',
        desc: '8% real appreciation like 2020-2024',
        rentImpact: calcRentOutcome(assumptions, { propertyAppreciation: 8 }) - baselineRent,
        sellImpact: 0,
        affectsRent: true,
        affectsSell: false,
      },
      {
        name: 'Capital Gains Tax Hike',
        desc: 'Federal + CA rate increases 5%',
        rentImpact: 0,
        sellImpact: -(assumptions.propertyValue * (1 - assumptions.sellingCosts / 100) * 0.05), // Rough approximation
        affectsRent: false,
        affectsSell: true,
      },
      {
        name: 'Depreciation Rules Change',
        desc: 'Rental depreciation deduction eliminated',
        rentImpact: calcRentOutcome(assumptions, { depreciation: false }) - baselineRent,
        sellImpact: 0,
        affectsRent: true,
        affectsSell: false,
      },
    ];

    return scenarios.map(s => ({
      ...s,
      winner: (() => {
        const newRent = baselineRent + s.rentImpact;
        const newSell = baselineSell + s.sellImpact;
        const diff = newRent - newSell;
        if (Math.abs(diff) < 50000) return 'Close';
        if (diff > 200000) return 'Rent (by a lot)';
        if (diff > 0) return 'Rent';
        if (diff < -200000) return 'Sell (by a lot)';
        return 'Sell';
      })(),
    }));
  }, [assumptions, baselineRent, baselineSell]);

  // Calculate the matrix outcomes
  const matrixData = useMemo(() => {
    const stockLevels = [
      { label: 'Poor', sublabel: '(4% real)', value: 4 },
      { label: 'Average', sublabel: '(7.5% real)', value: 7.5 },
      { label: 'Strong', sublabel: '(10%+ real)', value: 10 },
    ];
    const reLevels = [
      { label: 'Poor', sublabel: '(2% real)', value: 2 },
      { label: 'Average', sublabel: '(3.5% real)', value: 3.5 },
      { label: 'Strong', sublabel: '(6%+ real)', value: 6 },
    ];

    return stockLevels.map(stock => ({
      stockLevel: stock,
      cells: reLevels.map(re => {
        const rentValue = calcRentOutcome(assumptions, {
          propertyAppreciation: re.value,
          stockReturns: stock.value
        });
        const sellValue = calcSellOutcome(assumptions, { stockReturns: stock.value });
        const diff = rentValue - sellValue;

        let winner: string;
        let margin: string;
        let intensity: 'tie' | 'light' | 'dark';

        if (Math.abs(diff) < 50000) {
          winner = '~Tie';
          margin = `${formatCurrency(Math.abs(diff))} difference`;
          intensity = 'tie';
        } else if (diff > 0) {
          winner = 'Rent';
          margin = `by ${formatCurrency(diff)}`;
          intensity = diff > 400000 ? 'dark' : 'light';
        } else {
          winner = 'Sell';
          margin = `by ${formatCurrency(Math.abs(diff))}`;
          intensity = Math.abs(diff) > 400000 ? 'dark' : 'light';
        }

        return { winner, margin, intensity, rentValue, sellValue };
      }),
    }));
  }, [assumptions, formatCurrency]);

  return (
    <ReportSection id="comparison" title="Head-to-Head Comparison" subtitle="Running the numbers">
      {/* Sticky scenario selector */}
      <div className="sticky top-12 lg:top-14 z-30 bg-white border border-gray-200 shadow-sm -mx-4 px-4 py-3 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-3">
          <p className="hidden lg:block text-sm font-medium text-gray-600">
            Scenario: <span className="text-gray-900 font-semibold">
              {scenarioLabels[scenarioPreset].full}
            </span>
          </p>
          <p className="lg:hidden text-xs text-gray-500 mb-1">Select scenario:</p>
          <ScenarioToggle value={scenarioPreset} onChange={onScenarioChange} />
        </div>
      </div>

      <p className="text-gray-700 mb-6">
        Now let's put these options side by side. The outcome depends heavily on which assumptions you believe
        about future returns. We've built three scenarios—not "optimistic vs pessimistic" but rather tilting
        toward stocks outperforming vs real estate outperforming.
      </p>

      <div className="my-8">
        <ScenarioDetail preset={scenarioPreset} />
      </div>

      {/* Visual Comparison */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">10-Year Wealth Projection</h3>
        <div className="h-[300px]">
          <WealthChart
            data={wealthChartData}
            visibleScenarios={{ rent: true, sell100: false, sell80_20: true, sell60_40: false }}
            formatCurrency={formatCurrency}
          />
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Final Comparison</h3>
        <ComparisonBar
          rentValue={metrics.year10RentValue}
          sellValue={metrics.year10SellValue}
          formatCurrency={formatCurrency}
        />
      </div>

      {/* Detailed Breakdown Side-by-Side */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">How We Calculate These Numbers</h3>
        <p className="text-gray-700 mb-4">
          Both numbers represent <strong>net liquidation value</strong>—what you'd actually walk away with
          in cash after 10 years, including all taxes and transaction costs.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Keep & Rent Breakdown */}
          <div className="border border-gray-200 p-4">
            <p className="font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Keep & Rent</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Year 10 Property Value</span>
                <span className="font-medium">{formatCurrency(finalYear.rentScenario.propertyValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">+ Invested Rental Income</span>
                <span className="font-medium">{formatCurrency(finalYear.rentScenario.cumulativeReinvestedIncome)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-2">
                <span className="text-gray-700 font-medium">Gross Equity</span>
                <span className="font-medium">{formatCurrency(metrics.year10RentGrossEquity)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Selling Costs (~6%)</span>
                <span>−{formatCurrency(finalYear.rentScenario.sellingCostsAtExit)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Depreciation Recapture</span>
                <span>−{formatCurrency(finalYear.rentScenario.depreciationRecaptureTax)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Capital Gains Tax</span>
                <span>−{formatCurrency(finalYear.rentScenario.capitalGainsTax)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 mt-2">
                <span className="font-bold text-gray-900">Net Liquidation</span>
                <span className="font-bold text-gray-900">{formatCurrency(metrics.year10RentValue)}</span>
              </div>
            </div>
          </div>

          {/* Sell & Invest Breakdown */}
          <div className="border border-gray-200 p-4">
            <p className="font-semibold text-gray-900 mb-3 border-b border-gray-200 pb-2">Sell & Invest</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Sale Proceeds (after 6%)</span>
                <span className="font-medium">{formatCurrency(metrics.saleProceeds)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">× 10yr Growth (after tax drag)</span>
                <span className="font-medium">×{(metrics.year10SellGrossValue / metrics.saleProceeds).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-2">
                <span className="text-gray-700 font-medium">Gross Portfolio</span>
                <span className="font-medium">{formatCurrency(metrics.year10SellGrossValue)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>− Capital Gains Tax (if sold)</span>
                <span>−{formatCurrency(metrics.year10SellGrossValue - metrics.year10SellValue)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-300 pt-2 mt-2">
                <span className="font-bold text-gray-900">Net Liquidation</span>
                <span className="font-bold text-gray-900">{formatCurrency(metrics.year10SellValue)}</span>
              </div>
              <div className="mt-3 pt-2 border-t border-dashed border-gray-200">
                <p className="text-xs text-gray-500 italic">
                  Note: Stock portfolio has tax flexibility (gradual sales, donate, hold until death)
                  that could reduce effective tax significantly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Factor Comparison Table */}
      <div className="overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Factor</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-900">Keep & Rent</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">Sell & Invest</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 text-gray-600">10-Year Wealth</td>
              <td className="py-3 px-4 text-center font-medium">{formatCurrency(metrics.year10RentValue)}</td>
              <td className="py-3 px-4 text-center font-medium">{formatCurrency(metrics.year10SellValue)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 text-gray-600">Liquidity</td>
              <td className="py-3 px-4 text-center">Low</td>
              <td className="py-3 px-4 text-center">High</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 text-gray-600">Management Effort</td>
              <td className="py-3 px-4 text-center">High</td>
              <td className="py-3 px-4 text-center">None</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 text-gray-600">Tax Complexity</td>
              <td className="py-3 px-4 text-center">High</td>
              <td className="py-3 px-4 text-center">Low</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 text-gray-600">Concentration Risk</td>
              <td className="py-3 px-4 text-center">High (1 property)</td>
              <td className="py-3 px-4 text-center">Low (500+ companies)</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-3 px-4 text-gray-600">Inflation Hedge</td>
              <td className="py-3 px-4 text-center">Strong</td>
              <td className="py-3 px-4 text-center">Moderate</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* What If? Scenario Analysis */}
      <h3 className="text-xl font-semibold text-gray-800 mb-4">What If? Scenario Analysis</h3>
      <p className="text-gray-700 mb-4">
        The toggle above shows broad market assumptions. But what about specific events that could dramatically
        shift the outcome? Here's how various scenarios would affect the 10-year result:
      </p>
      <p className="text-gray-600 text-sm mb-6">
        <strong>Why does "Stock Boom" affect the rental scenario?</strong> Because we assume rental income is
        reinvested in stocks. If stocks return 10% instead of {assumptions.stockReturns}%, your accumulated rental
        income portfolio grows faster. Both scenarios benefit from stock performance, but the sell scenario benefits
        more since <em>all</em> the money is in stocks, not just the reinvested income.
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Scenario</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Rent Impact</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Sell Impact</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-500">Who Wins</th>
            </tr>
          </thead>
          <tbody>
            {whatIfScenarios.map((scenario, i) => (
              <tr key={i} className={`border-b border-gray-100 ${scenario.isTenantRisk ? 'bg-gray-50' : ''}`}>
                <td className="py-3 px-4">
                  <span className="font-medium text-gray-900">{scenario.name}</span>
                  <p className="text-xs text-gray-500">{scenario.desc}</p>
                </td>
                <td className="py-3 px-4 text-right">
                  {Math.abs(scenario.rentImpact) < 1000 ? (
                    <span className="text-gray-600">—</span>
                  ) : (
                    <span className={scenario.rentImpact !== 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                      {scenario.rentImpact > 0 ? '+' : ''}{formatCurrency(scenario.rentImpact)}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-right">
                  {Math.abs(scenario.sellImpact) < 1000 ? (
                    <span className="text-gray-600">—</span>
                  ) : (
                    <span className={scenario.sellImpact !== 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}>
                      {scenario.sellImpact > 0 ? '+' : ''}{formatCurrency(scenario.sellImpact)}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-gray-600">{scenario.winner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CalloutBox type="info" title="Reading This Table">
        <p className="mb-2">
          These aren't predictions—they're sensitivity tests. A few observations:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-2">
          <li>Most downside scenarios (tenants, repairs, tax changes) hurt the rental option specifically</li>
          <li>The rental option's main upside path requires Newport Beach to keep outperforming</li>
          <li>Stock market scenarios cut both ways—booms help sell, busts help rent</li>
        </ul>
      </CalloutBox>

      {/* The Complete Picture Matrix */}
      <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">The Complete Picture: Who Wins When?</h3>
      <p className="text-gray-700 mb-4">
        Here's a matrix showing who wins under every combination of stock and real estate performance.
        This summarizes all possible outcomes in one view:
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm border border-gray-300">
          <thead>
            <tr>
              <th className="bg-gray-100 border border-gray-300 p-2"></th>
              <th colSpan={3} className="bg-gray-100 border border-gray-300 p-2 text-center font-semibold text-gray-700">
                Real Estate Performance
              </th>
            </tr>
            <tr>
              <th className="bg-gray-100 border border-gray-300 p-2 text-left font-semibold text-gray-700">
                Stock Performance
              </th>
              {[
                { label: 'Poor', sublabel: '(2% real)' },
                { label: 'Average', sublabel: '(3.5% real)' },
                { label: 'Strong', sublabel: '(6%+ real)' },
              ].map((re, i) => (
                <th key={i} className="bg-gray-50 border border-gray-300 p-2 text-center text-gray-600 text-xs">
                  {re.label}<br /><span className="font-normal">{re.sublabel}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixData.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td className="bg-gray-50 border border-gray-300 p-2 font-medium text-gray-700 text-xs">
                  {row.stockLevel.label}<br /><span className="font-normal">{row.stockLevel.sublabel}</span>
                </td>
                {row.cells.map((cell, cellIdx) => {
                  const bgClass = cell.intensity === 'dark'
                    ? 'bg-gray-800 text-white'
                    : cell.intensity === 'tie'
                      ? 'bg-gray-100'
                      : cell.winner === 'Sell' ? 'bg-gray-200' : '';
                  const textClass = cell.intensity === 'dark'
                    ? ''
                    : cell.intensity === 'tie'
                      ? 'text-gray-600'
                      : 'text-gray-700';
                  const marginClass = cell.intensity === 'dark' ? 'text-gray-300' : 'text-gray-500';

                  return (
                    <td key={cellIdx} className={`border border-gray-300 p-3 text-center ${bgClass}`}>
                      <span className={`font-semibold ${textClass}`}>{cell.winner}</span>
                      <p className={`text-xs mt-1 ${marginClass}`}>{cell.margin}</p>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-gray-600 text-sm mb-4">
        <strong>Reading this table:</strong> The center cell (Stocks Average + RE Average) represents our "Balanced"
        scenario. Darker cells indicate larger advantages. These values update dynamically based on current assumptions
        including the {assumptions.maintenanceRate}% maintenance rate.
      </p>

      <CalloutBox type="insight" title="The Key Insight">
        <p>
          For renting to clearly win, Newport Beach real estate needs to significantly outperform stocks.
          If both perform at historical averages, selling comes out ahead due to lower expenses and
          the compounding advantage of a fully liquid portfolio.
        </p>
      </CalloutBox>

      <CalloutBox type="warning" title="Tax Methodology Note">
        <p className="mb-2">
          These comparisons use <strong>net liquidation values</strong>—what you'd actually walk away with
          in cash after 10 years, including all taxes and fees:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-2">
          <li><strong>Rent scenario:</strong> Property value + invested rental income - depreciation recapture tax (~37% on ~$196K) - capital gains tax on appreciation - selling costs (~6%)</li>
          <li><strong>Sell scenario:</strong> Portfolio value (after annual dividend/interest tax drag of ~0.5%) - capital gains tax if fully liquidated</li>
        </ul>
        <p>
          <strong>Important limitation:</strong> The sell scenario assumes full liquidation for comparison.
          In reality, you'd have significant <em>tax flexibility</em>—selling gradually to manage brackets,
          donating appreciated shares, or holding until death for stepped-up basis. This flexibility could
          save $100K+ in taxes over time, which isn't captured in these numbers.
        </p>
      </CalloutBox>

      <CalloutBox type="info" title="The Bottom Line">
        <p>
          Under the <strong>
            {scenarioLabels[scenarioPreset].full}
          </strong> assumptions, <strong>{rentAdvantage > 0 ? 'keeping and renting' : 'selling and investing'}</strong> comes
          out ahead by approximately {formatCurrency(Math.abs(rentAdvantage))} over 10 years—a{' '}
          {((Math.abs(rentAdvantage) / Math.min(metrics.year10RentValue, metrics.year10SellValue)) * 100).toFixed(1)}% difference.
        </p>
      </CalloutBox>
    </ReportSection>
  );
};
